import { Grid, GridRow } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { LessonEditStatus, use_PreviewTeachingLoadQuery } from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { LessonFormType } from '../types';
import { LessonBasicsForm } from './Basics/LessonBasicsForm';
import { LessonClassesForm } from './Classes/LessonClassesForm';
import { LessonUnitForm } from './LessonUnits/LessonUnitForm';
import { LessonRoomSupplyForm } from './RoomSupply/LessonRoomSupplyForm';
import { LessonTeacherForm } from './Teachers/LessonTeacherForm';
import { schema } from './validation/schema';

type LessonFormProps = {
  lessonUuid?: string | null;
  defaultInitialValues: Partial<LessonFormType>;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const DeputateLessonForm = observer(({ lessonUuid, defaultInitialValues, onLoad, onClose }: LessonFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();

  const { createLesson, updateLesson, lessons } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const lesson = lessons.find((l) => l.uuid === lessonUuid);
  const status = lesson?.editStatus;

  const initialValues: LessonFormType = lesson
    ? {
        uuid: lesson.uuid,
        subject: { uuid: lesson.subjectUuid, name: lesson.subject },
        studentCount: lesson.item.studentCount,
        elective: lesson.elective,
        lessonClasses: [...lesson.item.lessonClasses]
          .sort((a, b) => ((a.class.grade ?? -1) > (b.class.grade ?? -1) ? 1 : -1))
          .map((node) => {
            return {
              uuid: node.uuid,
              groups: node.groups,
              class: {
                uuid: node.class.uuid,
                name: node.class.name,
                color: node.class.color,
                grade: node.class.grade,
              },
            };
          }),
        comment: lesson.item.comment,
        lessonUnit: lesson.item.lessonUnits.map((node) => {
          return {
            uuid: node.uuid,
            duration: node.duration,
            count: node.count,
            subjectContainerUuid: node.subjectContainer?.uuid,
            blocked: status === LessonEditStatus.Blocked || status === LessonEditStatus.PlacedCards,
            lessonUnitType: node.subjectContainer?.uuid ? 'epoch' : 'subjectHour',
          };
        }),
        isClassTrip: lesson.item.isClassTrip,
        teachingLoadEnabled: lesson.teachingLoadEnabled,
        timetableEnabled: lesson.timetableEnabled,
        teachingLoadInfo: lesson.teachingLoadInfo,
        subTitle: lesson.item.subTitle,
        onlyInTimetableVersion: Boolean(lesson.onlyInTimetableVersion),
        roomSupply: lesson.item.roomSupply.map((room) => ({
          uuid: room.uuid,
          description: room.descriptions ?? '',
        })),
        // Ohne zugewiesene Lehrer zeigt das Formular die Lehrer-Platzhalter der Sollstunde als leere Zeilen.
        teachers:
          lesson.item.teachers.length === 0
            ? lesson.item.curriculumPersons.map((person) => ({
                description: person.description,
                present: person.present,
                teachingLoad: person.teachingLoad,
              }))
            : lesson.item.teachers.map((teacher) => ({
                description: teacher.description,
                present: teacher.present,
                teachingLoad: teacher.teachingLoad,
                teachingLoadHours: teacher.teachingLoadHours,
                writesCertificate: teacher.writesCertificate,
                person: {
                  uuid: teacher.uuid,
                  fullName: teacher.fullName,
                  displayNameShort: teacher.displayNameShort || teacher.fullName,
                  color: teacher.color,
                },
              })),
      }
    : {
        lessonClasses: [],
        ...defaultInitialValues,
        teachingLoadEnabled: true,
        timetableEnabled: true,
        lessonUnit: [],
        onlyInTimetableVersion: false,
      };

  const handleSubmit = async (values: LessonFormType, formikHelpers: FormikHelpers<LessonFormType>) => {
    onLoad(true);

    if (!lessonUuid) {
      const results = await createLesson(values);
      if (results.find((r) => r.error)) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await updateLesson(values, lessonUuid);
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    formikHelpers.resetForm();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      {({ values, errors, resetForm }) => (
        <Form>
          <Grid useFormGap>
            <GridRow>
              <LessonBasicsForm key={values.subject?.uuid} status={status} />
            </GridRow>
          </Grid>

          {!values.onlyInTimetableVersion && (
            <GridRow headline={t('classes.title', { count: 2 })}>
              <LessonClassesForm
                disabled={status === LessonEditStatus.Blocked || status === LessonEditStatus.PlacedCards}
              />
            </GridRow>
          )}

          <GridRow headline={t('lessonUnit.title', { count: 1 })}>
            <LessonUnitForm status={status} />
          </GridRow>

          <GridRow
            headline={
              lesson?.item.curriculumUuid
                ? `${t('persons.title', { count: 1 })} -> ${values.teachers?.length ?? 0}/${lesson.item.curriculumPersons.length}`
                : t('persons.title', { count: 1 })
            }
            tooltip={lesson?.item.curriculumUuid ? t('lesson.personsInCurriculum') : undefined}
          >
            <LessonTeacherForm
              disabled={status === LessonEditStatus.Blocked}
              defaults={{ writesCertificate: true, teachingLoad: true, present: true }}
            />
          </GridRow>

          <GridRow headline={t('rooms.stock')}>
            <LessonRoomSupplyForm disabled={status === LessonEditStatus.Blocked} />
          </GridRow>

          <ModalBottomButtons
            closeButton={{
              text: status === LessonEditStatus.Blocked ? t('common.close') : t('common.cancel'),
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
});
